import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Checkbox,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip,
  Tabs,
  Tab,
  Snackbar,
  Alert
} from "@mui/material";
import { Colors } from "../Colors";
import { LogItem, LogType } from "../types";
import { formatDistanceToNow } from "date-fns";
import { Link } from "react-router-dom";
import ChatIcon from "@mui/icons-material/Chat";
import SignoutHeader from "../subcomponents/SignoutHeader";
import { FrontendUser } from "../types";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { api } from "../utils/utils";

const getLogTypeColor = (
  logType: LogType
): { background: string; text: string } => {
  switch (logType) {
    case LogType.actionItem:
      return { background: "rgba(252, 213, 84, 0.12)", text: Colors.warning };
    case LogType.lateCancelFeePaid:
      return { background: "rgba(0, 172, 79, 0.12)", text: Colors.success };
    default:
      return { background: "rgba(252, 213, 84, 0.12)", text: Colors.warning };
  }
};

const Todo: React.FC = () => {
  const user: FrontendUser | null = useSelector(
    (state: RootState) => state.auth.user
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [todos, setTodos] = useState<LogItem[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const showAll = selectedTab === 1;
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success"
  });

  const observer = useRef<IntersectionObserver>();
  const lastTodoElementRef = useCallback(
    (node: HTMLElement | null) => {
      if (loading) return;

      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          fetchMoreTodos();
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  const fetchTodos = async () => {
    if (!user?.token) return;

    try {
      setLoading(true);
      setLastEvaluatedKey(null);

      const params = new URLSearchParams({
        page_size: "20"
      });

      if (!showAll) {
        params.append("completed", "false");
      }

      const response = await api.get(
        `/log-items/action-items?${params.toString()}`,
        user.token
      );

      setTodos(response.data.items || []);
      setHasMore(!!response.data.lastEvaluatedKey);
      setLastEvaluatedKey(response.data.lastEvaluatedKey);
    } catch (error) {
      console.dir(error, { depth: null, colors: true });
    } finally {
      setLoading(false);
    }
  };

  const fetchMoreTodos = async () => {
    if (!user?.token || !lastEvaluatedKey || !hasMore) return;

    try {
      setLoading(true);

      const params = new URLSearchParams({
        page_size: "20",
        last_evaluated_key: JSON.stringify(lastEvaluatedKey)
      });

      if (!showAll) {
        params.append("completed", "false");
      }

      const response = await api.get(
        `/log-items/action-items?${params.toString()}`,
        user.token
      );

      setTodos((prevTodos) => [...prevTodos, ...(response.data.items || [])]);
      setHasMore(!!response.data.lastEvaluatedKey);
      setLastEvaluatedKey(response.data.lastEvaluatedKey);
    } catch (error) {
      console.dir(error, { depth: null, colors: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.token) {
      setTodos([]);
      fetchTodos();
    }
  }, [user?.token, showAll]);

  const handleCheckboxChange = async (todo: LogItem) => {
    if (!user?.token) return;

    setTodos((prevTodos) =>
      prevTodos
        .map((prevTodo) =>
          prevTodo.logItemId === todo.logItemId
            ? { ...prevTodo, completed: !prevTodo.completed }
            : prevTodo
        )
        .sort((a, b) => {
          const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return dateB - dateA;
        })
    );

    try {
      await api.patch("/log-items", user.token, {
        logItemId: todo.logItemId,
        completed: !todo.completed
      });

      setTodos((prevTodos) =>
        prevTodos.filter((prevTodo) => showAll || !prevTodo.completed)
      );

      setSnackbar({
        open: true,
        message: `Task marked as ${
          !todo.completed ? "completed" : "incomplete"
        }`,
        severity: "success"
      });
    } catch (error) {
      console.dir(error, { depth: null, colors: true });
      setTodos((prevTodos) =>
        prevTodos.map((prevTodo) =>
          prevTodo.logItemId === todo.logItemId
            ? { ...prevTodo, completed: todo.completed }
            : prevTodo
        )
      );

      setSnackbar({
        open: true,
        message: "Failed to update task status",
        severity: "error"
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <Box
      sx={{
        marginTop: { xs: "75px", sm: 0 },
        color: "#FFF",
        minHeight: "100vh",
        pb: 4
      }}
    >
      <SignoutHeader />

      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="700" sx={{ mb: 0.5 }}>
          To Do List
        </Typography>
        <Typography variant="body1" sx={{ color: Colors.info }}>
          Track and complete action items from patient interactions.
        </Typography>
      </Box>

      <Tabs
        value={selectedTab}
        onChange={(_, value) => setSelectedTab(value)}
        textColor="primary"
        indicatorColor="primary"
        aria-label="task filter"
        sx={{
          mb: 3,
          minHeight: 36,
          borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
          "& .MuiTab-root": {
            textTransform: "none",
            minHeight: 36,
            fontSize: "0.875rem",
            color: Colors.info,
            "&.Mui-selected": { color: Colors.primary }
          }
        }}
      >
        <Tab label="Pending Tasks" />
        <Tab label="All Tasks" />
      </Tabs>

      {todos.length === 0 && !loading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: "120px"
          }}
        >
          <Typography variant="body1" sx={{ color: Colors.info }}>
            {showAll ? "No tasks found" : "No pending tasks"}
          </Typography>
        </Box>
      )}

      {todos.length > 0 && (
        <TableContainer
          component={Paper}
          sx={{
            backgroundColor: "rgba(255, 255, 255, 0.02)",
            border: "1px solid rgba(255, 255, 255, 0.08)",
            borderRadius: "12px",
            boxShadow: "none"
          }}
        >
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
                    color: Colors.info,
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    py: 2
                  }
                }}
              >
                <TableCell padding="checkbox" sx={{ pl: 2 }}>
                  Status
                </TableCell>
                <TableCell sx={{ width: "180px" }}>Created</TableCell>
                <TableCell sx={{ width: "180px" }}>Type</TableCell>
                <TableCell sx={{ width: "100px" }}>Patient</TableCell>
                <TableCell>Task</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {todos.map((todo, index) => (
                <TableRow
                  key={todo.logItemId}
                  ref={
                    index === todos.length - 1 ? lastTodoElementRef : undefined
                  }
                  sx={{
                    "& td": {
                      borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
                      py: 1.5
                    },
                    "&:last-of-type td": {
                      borderBottom: "none"
                    },
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.02)"
                    },
                    transition: "background-color 0.2s ease"
                  }}
                >
                  <TableCell padding="checkbox" sx={{ pl: 2 }}>
                    <Checkbox
                      checked={todo.completed}
                      onChange={() => handleCheckboxChange(todo)}
                      size="small"
                      sx={{
                        color: Colors.info,
                        "&.Mui-checked": {
                          color: Colors.success
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip
                      title={
                        todo.createdAt
                          ? new Date(todo.createdAt).toLocaleString("en-US", {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit"
                            })
                          : "Unknown time"
                      }
                      placement="top"
                    >
                      <Typography
                        sx={{
                          fontSize: "0.875rem",
                          textDecoration: todo.completed
                            ? "line-through"
                            : "none",
                          color: todo.completed ? Colors.info : "#FFF",
                          transition: "all 0.2s ease"
                        }}
                      >
                        {todo.createdAt
                          ? formatDistanceToNow(new Date(todo.createdAt), {
                              addSuffix: true
                            })
                          : "Unknown time"}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={
                        todo.completed
                          ? "Completed"
                          : todo.logType.replace(/-/g, " ")
                      }
                      size="small"
                      sx={{
                        height: "22px",
                        backgroundColor: todo.completed
                          ? "rgba(0, 172, 79, 0.12)"
                          : getLogTypeColor(todo.logType).background,
                        color: todo.completed
                          ? Colors.success
                          : getLogTypeColor(todo.logType).text,
                        fontWeight: 600,
                        fontSize: "0.7rem",
                        letterSpacing: "0.3px",
                        textTransform: "uppercase",
                        border: "none",
                        "& .MuiChip-label": {
                          px: 1.25
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Link
                      to={`/dashboard/patients/${todo.patientId}`}
                      style={{ textDecoration: "none" }}
                    >
                      <Tooltip title="View patient">
                        <IconButton
                          size="small"
                          sx={{
                            color: Colors.info,
                            "&:hover": {
                              color: Colors.primary,
                              backgroundColor: "rgba(94, 120, 238, 0.08)"
                            }
                          }}
                        >
                          <ChatIcon sx={{ fontSize: "1.1rem" }} />
                        </IconButton>
                      </Tooltip>
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Typography
                      sx={{
                        fontSize: "0.875rem",
                        textDecoration: todo.completed ? "line-through" : "none",
                        color: todo.completed ? Colors.info : "#FFF",
                        transition: "all 0.2s ease"
                      }}
                    >
                      {todo.logDescription}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {loading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            flexDirection: "column",
            alignItems: "center",
            gap: "1rem",
            marginTop: todos.length === 0 ? "8rem" : "2rem",
            marginBottom: "2rem"
          }}
        >
          <CircularProgress sx={{ color: Colors.info }} size={28} />
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            width: "100%",
            "& .MuiAlert-message": { color: "#fff" }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Todo;
